import {
  generateNumbersInRange,
  randomiseList,
} from '@freelancer/datastore/testing';
import { RoleApi } from 'api-typings/common/common';
import {
  generateFreelancerReputationDataObjects,
  GenerateFreelancerReputationDataOption,
  GenerateFreelancerReputationDataOptions,
  generateReputationDataObject,
} from '../reputation/reputation-data.seed';
import { FreelancerReputation } from './freelancer-reputations.model';

interface Range {
  readonly min: number;
  readonly max: number;
}

export interface GenerateFreelancerReputationsOptions {
  readonly freelancerIds: ReadonlyArray<number>;
  readonly reputationOptions?: GenerateFreelancerReputationDataOptions;
  readonly earningsScore?: Range;
}

/** Keep in sync with generateFreelancerReputationsRehireRatesObjects */
export function generateFreelancerReputationsObjects({
  freelancerIds,
  reputationOptions = {},
  earningsScore = { min: 3, max: 10 },
}: GenerateFreelancerReputationsOptions): ReadonlyArray<FreelancerReputation> {
  const entireHistories = generateFreelancerReputationDataObjects({
    ...reputationOptions,
    seed: 'entireHistory',
    numberToMake: freelancerIds.length,
  });

  const earningsScores = randomiseList(
    generateNumbersInRange(
      earningsScore.min,
      earningsScore.max,
      freelancerIds.length,
    ),
    'earningsScore',
  );

  return freelancerIds.map((freelancerId, i) => ({
    id: freelancerId,
    role: RoleApi.FREELANCER,
    // Set value to 0 if you've not done any work.
    earningsScore: entireHistories[i].all === 0 ? 0 : earningsScores[i],
    entireHistory: entireHistories[i],
  }));
}

export interface GenerateFreelancerReputationOptions {
  readonly freelancerId: number;
  readonly reputationOptions?: Partial<GenerateFreelancerReputationDataOption>;
  readonly earningsScore?: number;
}

export const defaultReputations: GenerateFreelancerReputationDataOption = {
  total: 75,
  completePercentage: 0.8,
  reviewsPercentage: 0.8,
  incompleteReviewsPercentage: 0.8,
  communication: 0.8,
  expertise: 0.8,
  hireAgain: 0.8,
  quality: 0.8,
  professionalism: 0.8,
  onBudgetPercentage: 0.8,
  onTimePercentage: 0.8,
};

/** Keep in sync with generateFreelancerReputationsRehireRatesObject */
export function generateFreelancerReputationsObject({
  freelancerId,
  reputationOptions = {},
  earningsScore = 7,
}: GenerateFreelancerReputationOptions): FreelancerReputation {
  return {
    id: freelancerId,
    earningsScore,
    role: RoleApi.FREELANCER,
    entireHistory: generateReputationDataObject({
      ...defaultReputations,
      ...reputationOptions,
    }),
  };
}
